import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FileText } from "lucide-react";
import uploadIcon from "../../assets/svg/upload_file.svg";
import closeAttachment from "../../assets/svg/close-red.svg";
import { submitVehicleKMEntry, saveKMAttachmentApi } from "./vehicle-api";
import { sanitizeInput, sanitizeTextarea, validateFileType, validateFileSize } from "../common/security-utils";

/**
 * KMEntryModal Component
 * Handles adding kilometer entries for a specific vehicle.
 * Implements VAPT security measures and optimized performance.
 */
const KMEntryModal = ({ show, id, lastKM, onHide, onSubmit }) => {
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // Form states
  const [currentKm, setCurrentKm] = useState("");
  const [lastKmRecord, setLastKmRecord] = useState(lastKM || "0");
  const [driverName, setDriverName] = useState("");
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const [errorMsg, setErrorMsg] = useState({});

  // Sync lastKM prop
  useEffect(() => {
    setLastKmRecord(lastKM || "0");
  }, [lastKM]);

  const resetForm = useCallback(() => {
    setCurrentKm("");
    setLastKmRecord(lastKM || "0");
    setDriverName("");
    setDescription("");
    setAttachments([]);
    setAttachmentPreview(null);
    setErrorMsg({});
  }, [lastKM]);

  const handleClose = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!currentKm) errors.current_km = "Current KM is required";
    if (!driverName.trim()) errors.driver_name = "Driver Name is required";

    // VAPT: Basic logic validation
    if (parseFloat(currentKm) < parseFloat(lastKmRecord)) {
      errors.current_km = "Current KM cannot be less than last recorded KM";
    }

    setErrorMsg(errors);
    return Object.keys(errors).length === 0;
  }, [currentKm, lastKmRecord, driverName]);

  const processFiles = useCallback((files) => {
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSizeMB = 5;

    const validFiles = files.filter(file => {
      if (!validateFileType(file, allowedTypes)) {
        setErrorMsg(prev => ({ ...prev, attachments: "Invalid file type. Only JPG, PNG, and PDF allowed." }));
        return false;
      }
      if (!validateFileSize(file, maxSizeMB)) {
        setErrorMsg(prev => ({ ...prev, attachments: `File size exceeds ${maxSizeMB}MB limit.` }));
        return false;
      }
      return true;
    });

    const newAttachments = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isPdf: file.type === "application/pdf",
      name: file.name,
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
    if (newAttachments.length > 0 && !attachmentPreview) {
      setAttachmentPreview(newAttachments[0].preview);
    }
  }, [attachmentPreview]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeAttachment = useCallback((index) => {
    setAttachments(prev => {
      const updated = prev.filter((_, idx) => idx !== index);
      if (updated.length === 0) {
        setAttachmentPreview(null);
      } else if (attachmentPreview === prev[index].preview) {
        setAttachmentPreview(updated[0].preview);
      }
      return updated;
    });
  }, [attachmentPreview]);

  const saveAttachment = async (parentId) => {
    const fileToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });

    try {
      const imagesBase64 = await Promise.all(attachments.map((img) => fileToBase64(img.file)));
      const response = await saveKMAttachmentApi(parentId, { attachment: imagesBase64 });
      if (response?.data?.success) {
        resetForm();
        onSubmit(response.data);
      } else {
        onSubmit({ success: true, message: "KM Entry saved, but attachment upload failed." });
      }
    } catch (err) {
      onSubmit({ success: true, message: "KM Entry saved, but an error occurred during attachment upload." });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      vehicle_id: id,
      last_km_record: lastKmRecord,
      current_km: sanitizeInput(String(currentKm)),
      last_km: lastKmRecord,
      driver_name: sanitizeInput(driverName),
      description: sanitizeTextarea(description),
    };

    setIsLoading(true);
    try {
      const response = await submitVehicleKMEntry(payload);
      if (response?.data?.success) {
        if (attachments.length === 0) {
          resetForm();
          onSubmit(response.data);
        } else {
          await saveAttachment(response.data.id);
        }
      } else if (response?.status === 400 && response?.data?.error) {
        const backendErrors = {};
        Object.entries(response.data.error).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrorMsg(backendErrors);
      } else {
        onSubmit(response?.data || { success: false, message: "Submission failed" });
      }
    } catch (err) {
      setErrorMsg({ general: "A network error occurred. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="md" centered className="km-entry-modal">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-700 fs-18 text-21272C">Add Kilometer</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        <div className="row">
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Last KM Recorded</label>
            <input
              type="text"
              className="form-control bg-light"
              value={lastKmRecord}
              readOnly
              style={{ cursor: "not-allowed" }}
            />
          </div>
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Current KM</label>
            <input
              type="text"
              className={`form-control ${errorMsg.current_km ? 'is-invalid' : ''}`}
              placeholder="Enter current KM"
              value={currentKm}
              onChange={(e) => setCurrentKm(e.target.value.replace(/[^0-9.]/g, ""))}
            />
            {errorMsg.current_km && <small className="text-danger mt-1 d-block">{errorMsg.current_km}</small>}
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fs-13 fw-600 mb-2">Driver Name</label>
          <input
            type="text"
            className={`form-control ${errorMsg.driver_name ? 'is-invalid' : ''}`}
            placeholder="Enter driver name"
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
          />
          {errorMsg.driver_name && <small className="text-danger mt-1 d-block">{errorMsg.driver_name}</small>}
        </div>

        <div className="mb-4">
          <label className="form-label fs-13 fw-600 mb-2">Description</label>
          <textarea
            className="form-control"
            rows="3"
            placeholder="Add relevant notes or details..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ resize: "none" }}
          />
        </div>

        <div className="mb-2">
          <label className="form-label fs-13 fw-600 mb-2">Attachment</label>
          <div
            className="rounded-3 p-4 d-flex flex-column align-items-center justify-content-center w-100 transition-all"
            style={{
              minHeight: 140,
              cursor: "pointer",
              border: "2px dashed #D1D5DB",
              backgroundColor: "#F9FAFB",
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDragDrop}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "#3469E7"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "#D1D5DB"}
          >
            {attachmentPreview ? (
              attachments.find((a) => a.preview === attachmentPreview)?.isPdf ? (
                <div className="text-center">
                  <FileText size={40} color="#3469E7" />
                  <p className="fs-12 text-dark mb-0 mt-1">
                    {attachments.find((a) => a.preview === attachmentPreview)?.name || "Document.pdf"}
                  </p>
                </div>
              ) : (
                <img
                  src={attachmentPreview}
                  alt="preview"
                  style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain", borderRadius: "8px" }}
                />
              )
            ) : (
              <div className="text-center">
                <img src={uploadIcon} alt="upload" className="mb-2" style={{ width: 32, height: 32, opacity: 0.6 }} />
                <p className="fs-13 fw-500 text-dark mb-1">
                  Click to upload or <span className="text-primary">drag and drop</span>
                </p>
                <p className="text-muted fs-11 mb-0">PDF, PNG, JPG (max. 5MB)</p>
              </div>
            )}
          </div>
          {errorMsg.attachments && <small className="text-danger mt-2 d-block">{errorMsg.attachments}</small>}

          {attachments.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {attachments.map((att, idx) => (
                <div key={idx} className="position-relative" style={{ width: 64, height: 64 }}>
                  {att.isPdf ? (
                    <div
                      className="w-100 h-100 d-flex align-items-center justify-content-center rounded-2 border bg-light"
                      style={{ cursor: "pointer" }}
                      onClick={() => setAttachmentPreview(att.preview)}
                    >
                      <FileText size={24} color="#3469E7" />
                    </div>
                  ) : (
                    <img
                      src={att.preview}
                      alt="thumbnail"
                      className="w-100 h-100 object-fit-cover rounded-2 border"
                      style={{ cursor: "pointer" }}
                      onClick={() => setAttachmentPreview(att.preview)}
                    />
                  )}
                  <button
                    className="btn btn-sm btn-white position-absolute d-flex align-items-center justify-content-center shadow-sm"
                    style={{
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      padding: 0,
                      borderRadius: "50%",
                      border: "1px solid #E5E7EB",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAttachment(idx);
                    }}
                  >
                    <img src={closeAttachment} alt="remove" style={{ width: 10 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileUpload} style={{ display: "none" }} />
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 pb-4 px-4">
        <Button variant="white" onClick={handleClose} className="rounded-pill px-4 border fs-14 fw-600 me-2" style={{ color: "#4B5563" }}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          className="rounded-pill px-5 fs-14 fw-600 shadow-sm transition-all"
          disabled={isLoading}
          style={{ backgroundColor: "#3469E7", border: "none" }}
        >
          {isLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Saving...</span>
            </div>
          ) : "Save KM"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default KMEntryModal;
